import { useLazyQuery } from '@apollo/client';
import {
  AptunoApolloLink,
  AptunoError,
  BaseQueryResponse2,
  ResourceNotFoundError,
  Response,
  ResponseApolloErrorMapping,
} from '@contexts/Shared';
import React, { useCallback, useMemo } from 'react';
import {
  mapExposedEstateApplicationDtoToDomain,
  mapExposedEstateDtoToDomain,
} from '../data-mappers';
import {
  MyApplicationForEstateResponseDto,
  GetEstateApplicationDTO,
  MyEstateApplicationsDTO,
} from '../dtos';
import {
  existsActiveEstateApplication,
  exposedEstate,
  GetEstateApplicationSchema,
  COUNT_ESTATE_APPLICATIONS,
  MyApplicationForEstateSchema,
  MyEstateApplicationsSchema,
  userHasAnActiveAndNotCalculatedEstateApplication,
} from '../schemas';
import {
  EstateApplicationQueryActions,
  ExposedEstate,
  EstateApplication,
  countEstateApplicationosQuery,
  GetExposedEstateApplicationQuery,
  GetExposedEstateQuery,
  GetExposedEstateT,
  ValidateIfExistsActiveEstateApplicationQuery,
  ValidateIfExistsActiveEstateApplicationT,
  GetExposedEstateApplicationByTenantAndEstateQuery,
  GetExposedUserEstateApplicationsQuery,
} from './../../../domain';
import { CountEstateApplicationsT } from './../../../domain/ports/estate-application-query.actions.port';

export const GqlEstateApplicationQueryActions: EstateApplicationQueryActions = {
  useValidateIfExistsActiveEstateApplication:
    (): ValidateIfExistsActiveEstateApplicationT => {
      const [action, { data, loading, error, refetch }] = useLazyQuery<{
        existActiveEstateApplication: boolean;
      }>(existsActiveEstateApplication, {
        errorPolicy: 'all',
        context: { clientName: AptunoApolloLink.CREDIT_STUDY },
      });

      const errorResult: AptunoError | undefined = useMemo(
        () => (error ? ResponseApolloErrorMapping(error) : undefined),
        [error]
      );

      const recall = useCallback(
        async (): Promise<boolean> =>
          refetch().then(
            (response) => response.data.existActiveEstateApplication
          ),
        [refetch]
      );

      return {
        loading,
        recall,
        error: errorResult,
        existsActiveEstateApplication:
          data && data.existActiveEstateApplication,
        validateIfExistsActiveEstateApplication: (
          query: ValidateIfExistsActiveEstateApplicationQuery
        ) =>
          action({
            variables: {
              estateId: query.estateId.value,
            },
          }),
      };
    },

  useGetEstateApplication: (): BaseQueryResponse2<
    GetExposedEstateApplicationQuery,
    EstateApplication
  > => {
    const [action, { data, loading, error, refetch }] =
      useLazyQuery<GetEstateApplicationDTO>(GetEstateApplicationSchema, {
        errorPolicy: 'all',
        context: { clientName: AptunoApolloLink.CREDIT_STUDY },
      });

    const result: Response<EstateApplication> = React.useMemo(
      () =>
        !!error
          ? new Response.Failure(ResponseApolloErrorMapping(error))
          : !data
          ? Response.notPerformed
          : !!data.getEstateApplication?.value
          ? new Response.Success(
              mapExposedEstateApplicationDtoToDomain(
                data.getEstateApplication.value
              )
            )
          : new Response.Failure(
              new ResourceNotFoundError('EstateApplication')
            ),
      [data, error]
    );

    const recall = React.useCallback(
      async (): Promise<void> => refetch().then(() => void 0),
      [refetch]
    );

    return {
      loading,
      recall,
      result,
      get: (query: GetExposedEstateApplicationQuery) =>
        action({
          variables: {
            estateApplicationId: query.estateApplicationId.value,
          },
        }),
    };
  },

  useGetExposedEstate: (): GetExposedEstateT => {
    const [action, { data, loading, error, refetch }] = useLazyQuery<{
      exposedEstate: { estateId: number; legalId: string };
    }>(exposedEstate, {
      errorPolicy: 'all',
    });

    const exposedEstateResult: ExposedEstate | undefined = useMemo(
      () =>
        data && data.exposedEstate
          ? mapExposedEstateDtoToDomain(data.exposedEstate)
          : undefined,
      [data]
    );

    const exposedEstateError: AptunoError | undefined = useMemo(
      () => (error ? ResponseApolloErrorMapping(error) : undefined),
      [error]
    );

    const recall = useCallback(
      async (): Promise<ExposedEstate | undefined> =>
        refetch().then(
          (response) =>
            response.data?.exposedEstate &&
            mapExposedEstateDtoToDomain(response.data.exposedEstate)
        ),
      [refetch]
    );

    return {
      loading,
      recall,
      error: exposedEstateError,
      exposedEstate: exposedEstateResult,
      getExposedEstate: (query: GetExposedEstateQuery) =>
        action({
          variables: {
            estateId: query.estateId.value,
          },
        }),
    };
  },

  useCountEstateApplications: (): CountEstateApplicationsT => {
    const [action, { data, loading, error, refetch }] = useLazyQuery<{
      countEstateApplicationsForLastMonth: number;
    }>(COUNT_ESTATE_APPLICATIONS, {
      errorPolicy: 'all',
      context: { clientName: AptunoApolloLink.CREDIT_STUDY },
    });

    const errorResult: AptunoError | undefined = useMemo(
      () => (error ? ResponseApolloErrorMapping(error) : undefined),
      [error]
    );

    const recall = useCallback(
      async (): Promise<number> =>
        refetch().then(
          (response) => response.data.countEstateApplicationsForLastMonth
        ),
      [refetch]
    );

    return {
      loading,
      recall,
      error: errorResult,
      estateApplicationsCount: data?.countEstateApplicationsForLastMonth,
      countEstateApplications: (query: countEstateApplicationosQuery) =>
        action({
          variables: {
            estateId: query.estateId,
          },
        }),
    };
  },

  useGetMyApplicationForEstate: (): BaseQueryResponse2<
    GetExposedEstateApplicationByTenantAndEstateQuery,
    EstateApplication
  > => {
    const [action, { data, loading, error, refetch }] =
      useLazyQuery<MyApplicationForEstateResponseDto>(
        MyApplicationForEstateSchema,
        {
          errorPolicy: 'all',
          context: { clientName: AptunoApolloLink.CREDIT_STUDY },
        }
      );

    const result: Response<EstateApplication> = useMemo(
      () =>
        !!error
          ? new Response.Failure(ResponseApolloErrorMapping(error))
          : !data
          ? Response.notPerformed
          : !!data.myApplicationForEstate?.value
          ? new Response.Success(
              mapExposedEstateApplicationDtoToDomain(
                data.myApplicationForEstate.value
              )
            )
          : new Response.Failure(
              new ResourceNotFoundError('estateApplicationByTenantAndEstate')
            ),
      [data, error]
    );

    const recall = useCallback(
      async (): Promise<void> => refetch().then(() => void 0),
      [refetch]
    );

    return {
      loading,
      recall,
      result,
      get: (query: GetExposedEstateApplicationByTenantAndEstateQuery) =>
        action({
          variables: {
            estateId: query.estateId.value,
          },
        }),
    };
  },

  useGetMyEstateApplications: function (): BaseQueryResponse2<
    GetExposedUserEstateApplicationsQuery,
    EstateApplication[]
  > {
    const [action, { data, loading, error, refetch }] =
      useLazyQuery<MyEstateApplicationsDTO>(MyEstateApplicationsSchema, {
        errorPolicy: 'all',
        context: { clientName: AptunoApolloLink.CREDIT_STUDY },
      });

    const result: Response<EstateApplication[]> = useMemo(
      () =>
        !!error
          ? new Response.Failure(ResponseApolloErrorMapping(error))
          : !data
          ? Response.notPerformed
          : !!data.myEstateApplications && data.myEstateApplications.length > 0
          ? new Response.Success(
              data.myEstateApplications.map((estateApplicationInfo) =>
                mapExposedEstateApplicationDtoToDomain(estateApplicationInfo)
              )
            )
          : new Response.Failure(
              new ResourceNotFoundError('getExposedUserEstateApplications')
            ),
      [data, error]
    );

    const recall = useCallback(
      async (): Promise<void> => refetch().then(() => void 0),
      [refetch]
    );

    return {
      loading,
      recall,
      result,
      get: (query: GetExposedUserEstateApplicationsQuery) =>
        action({
          variables: {
            page: query.page,
          },
        }),
    };
  },

  useValidateIfUserHasAnActiveAndNotCalculatedEstateApplication:
    function (): BaseQueryResponse2<void, boolean> {
      const [action, { data, loading, error, refetch }] = useLazyQuery<{
        userHasAnActiveAndNotCalculatedEstateApplication: boolean;
      }>(userHasAnActiveAndNotCalculatedEstateApplication, {
        errorPolicy: 'all',
        context: { clientName: AptunoApolloLink.CREDIT_STUDY },
      });

      const result: Response<boolean> = useMemo(
        () =>
          !!error
            ? new Response.Failure(ResponseApolloErrorMapping(error))
            : !data
            ? Response.notPerformed
            : new Response.Success(
                !!data.userHasAnActiveAndNotCalculatedEstateApplication
              ),
        [data, error]
      );

      const recall = useCallback(
        async (): Promise<void> => refetch().then(() => void 0),
        [refetch]
      );

      return {
        loading,
        recall,
        result,
        get: () => action(),
      };
    },
};
